//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  CuralateCatalogProduct,
  CuralateProductImage,
  TrackingParam,
  TrackingParamGroup,
  TrackingParams,
  TrackingParamsData,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import { Product, UiaProductTagRequest } from '@models/IItemMetadata';
import { BV_PRODUCT_PREFIX } from '@uia/products/constants/UiaProductConstants';
import { isDefined } from '@utils/filterPredicates';

/**
 * Determines if a product is a BV product based on its style ID.
 *
 */
export const getIsBvProduct = (productStyleId: string | undefined) => {
  return productStyleId?.startsWith(BV_PRODUCT_PREFIX);
};

/**
 * Extracts image URLs from a Product or CuralateCatalogProduct object.
 *
 * Prioritizes a provided uploaded image URL, then attempts to extract from the product's images array,
 * and finally falls back to a single image URL if available directly on the product.
 *
 */
export const getProductOrTagImageUrls = (
  product: Product | CuralateCatalogProduct,
  uploadedImage?: CuralateProductImage,
) => {
  if (uploadedImage) {
    return [uploadedImage.imageUrl];
  }

  if ('images' in product) {
    return product.images.map((image) => image.imageUrl);
  } else if ('imageUrl' in product) {
    return [product.imageUrl];
  }

  return [];
};

/**
 * Calculates the difference between two sets of tracking parameters, identifying parameters in the override set
 * that have different values or autoSet properties compared to the original set.
 *
 * @returns An array of tracking parameters from the override set that differ from the original set.
 */
const getOverrideParamsDiff = ({
  originalParams,
  overrideParams,
}: {
  originalParams: TrackingParams;
  overrideParams: TrackingParams;
}): TrackingParam[] => {
  const allOriginalParams = originalParams.paramGroups.flatMap(
    (group) => group.params,
  );
  const allOverrideParams = overrideParams.paramGroups.flatMap(
    (group) => group.params,
  );

  const originalParamMap = new Map(
    allOriginalParams.map((param) => [param.name, param]),
  );

  return allOverrideParams.filter((param) => {
    const originalParam = originalParamMap.get(param.name);

    if (!originalParam) {
      return false;
    }

    return (
      param.value !== originalParam.value ||
      param.autoSet !== originalParam.autoSet
    );
  });
};

/**
 * Calculates the tracking parameters that need to be overridden based on the difference between
 * original and override tracking parameters, while also handling curalateReserved parameters.
 *
 * It ensures that curalateReserved parameters are not written out as overrides unless they were
 * originally overridden and preserves their original values and autoSet status if they were.
 *
 * @returns An array of TrackingParam objects representing the parameters to override,
 * excluding curalateReserved parameters unless they were originally overridden.
 */
export const getTrackingOverrideParams = ({
  originalTrackingParams,
  overrideTrackingParams,
  product,
}: {
  originalTrackingParams: TrackingParams;
  overrideTrackingParams: TrackingParams;
  product: UiaProductTagRequest;
}): TrackingParam[] => {
  const overrideParams = getOverrideParamsDiff({
    originalParams: originalTrackingParams,
    overrideParams: overrideTrackingParams,
  });

  const trackingParamsData = product.trackingParamsData;

  const processedOverrideParams = trackingParamsData
    ? (() => {
        const trackingParamsJson = JSON.parse(
          trackingParamsData,
        ) as TrackingParamsData;
        const originalOverrideNames =
          trackingParamsJson.overrides
            ?.map((override) => override.name)
            .filter(isDefined) ?? [];

        const originalOverrideMap = new Map(
          (trackingParamsJson.overrides ?? [])
            .filter(isDefined)
            .map((override) => [override.name, override]),
        );

        return overrideParams.map((localParam) => {
          if (
            localParam.curalateReserved &&
            originalOverrideNames.includes(localParam.name)
          ) {
            const originalOverrideParam = originalOverrideMap.get(
              localParam.name,
            );

            if (originalOverrideParam) {
              return {
                ...localParam,
                value: originalOverrideParam.value ?? localParam.value,
                autoSet: originalOverrideParam.autoSet ?? localParam.autoSet,
                curalateReserved: false, // An overridden parameter should never be reserved
              };
            }
          }

          return localParam;
        });
      })()
    : overrideParams;

  // This ensure a curalateReserved parameter is never written out as an override
  return processedOverrideParams.filter(
    (localParam) => !localParam.curalateReserved,
  );
};

/**
 * Generates tracking settings by merging original tracking params, overrides, and product specific overrides
 *
 */
export const getTrackingSettings = ({
  isTrackingParamsEnabled,
  originalTrackingParams,
  overrideTrackingParams,
  product,
  trackingParamsVersion,
}: {
  isTrackingParamsEnabled: boolean;
  originalTrackingParams: TrackingParams;
  overrideTrackingParams: TrackingParams;
  product: UiaProductTagRequest;
  trackingParamsVersion: number | undefined;
}) => {
  if (!isTrackingParamsEnabled) {
    return {
      trackingParamsVersion: undefined,
      trackingParamsData: undefined,
    };
  }

  return {
    trackingParamsVersion,
    trackingParamsData: JSON.stringify({
      overrides: getTrackingOverrideParams({
        originalTrackingParams,
        overrideTrackingParams,
        product,
      }),
    }),
  };
};

/**
 * This function takes a base set of tracking parameter groups and a string containing JSON data
 * with potential overrides. If the overrides exist, they are applied to the corresponding
 * parameters in the base groups.
 *
 * - If trackingParamsData is undefined or empty, the original baseParamGroups are returned unchanged
 * - If trackingParamsData cannot be parsed as valid JSON, the original baseParamGroups are returned unchanged
 */
export const mergeTrackingParamsWithOverrides = (
  baseParamGroups: TrackingParamGroup[],
  trackingParamsData: string | undefined,
): TrackingParamGroup[] => {
  if (!trackingParamsData) {
    return baseParamGroups;
  }

  try {
    const parsedOverrides = JSON.parse(
      trackingParamsData,
    ) as TrackingParamsData;
    const overrideMap = (parsedOverrides.overrides ?? []).reduce(
      (overridesTuple, override) => {
        if (isDefined(override.name)) {
          overridesTuple.set(override.name, override);
        }

        return overridesTuple;
      },
      new Map<string, Partial<TrackingParam>>(),
    );

    if (overrideMap.size === 0) {
      return baseParamGroups;
    }

    return baseParamGroups.map((group) => ({
      ...group,
      params: group.params.map((param) => {
        const override = overrideMap.get(param.name);

        if (override) {
          return {
            ...param,
            value: override.value ?? param.value,
            autoSet: override.autoSet ?? param.autoSet,
          };
        }

        return param;
      }),
    }));
  } catch (_e) {
    return baseParamGroups;
  }
};
